import React, { useState, useEffect, Suspense, lazy, useMemo } from 'react';

import { ThemeProvider } from '@mui/material/styles';
import { MRT_RowData, MRT_ColumnDef } from 'material-react-table';
import CircularProgress from '@mui/material/CircularProgress';
const MaterialReactTable = lazy(() =>
  import('material-react-table').then((module) => ({
    default: module.MaterialReactTable,
  })),
);
const PopupDialog = lazy(() => import('./PopupDialog'));

import Papa from 'papaparse';
import Linkify from 'linkify-react';

import { Results } from '../types/types';
import { getTableOptions } from '../utils/tableOptions';
import { linkifyOptions } from '../utils/linkifyOptions';
import { useAndFiltering, useOrFiltering } from '../utils/searchFiltering';
import { getLocalizedLanguagePack } from './../utils/language';
import { filterIncludedColumns, filterInitialVisibleColumns } from '../utils/columnFiltering';
import { getTheme } from './../utils/theme';

import './../styles/styleFixes.css';
import './../styles/styleChanges.css';

export default function Table() {
  const [columns, setColumns] = useState<MRT_ColumnDef<any>[]>([]);
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [dialogRowData, setDialogRowData] = useState<MRT_RowData | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});
  const { requiredOptions, optionalOptions } = useMemo(() => getTableOptions(), []);
  const theme = useMemo(() => getTheme(), []);
  const localization = useMemo(() => getLocalizedLanguagePack(), []);

  useEffect(() => {
    async function prepareTable() {
      const response = await fetch(requiredOptions._tableUrl);
      const csvText = await response.text();

      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          // All results
          const originalResults: Results = {
            data: results.data,
            fields: results.meta.fields,
          };

          // Filtered results
          const filteredResults = filterIncludedColumns(
            originalResults,
            optionalOptions._columnsToFetch,
          );

          // Set initial visible columns
          setVisibleColumns(
            filterInitialVisibleColumns(originalResults, optionalOptions._initialVisibleColumns),
          );

          // Generate each column for Material-React-Table
          const generatedColumns = filteredResults.fields.map((field) => {
            // Extract unique, non-empty values from the column
            const uniqueValues = [...new Set(filteredResults.data.map((row) => row[field]))].filter(
              (value) => value !== '',
            );

            // Column configuration
            return {
              header: field,
              accessorKey: field,
              filterVariant: 'multi-select',
              filterSelectOptions: uniqueValues,
              enableColumnFilter: true,
              filterFn:
                optionalOptions._filteringCombiner === 'OR' ? useOrFiltering : useAndFiltering,
              Cell: ({ cell }) => <Linkify options={linkifyOptions}>{cell.getValue()}</Linkify>,
            };
          });

          // Set info for Material-React-Table
          setColumns(generatedColumns);
          setData(filteredResults.data);
        },
      });
    }
    prepareTable();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Suspense fallback={<CircularProgress />}>
        {Object.keys(visibleColumns).length == 0 ? (
          <CircularProgress />
        ) : (
          <MaterialReactTable
            columns={columns}
            data={data}
            initialState={{
              pagination: { pageIndex: 0, pageSize: optionalOptions._initialRowsPerPage },
              density: optionalOptions._initialDensity,
              showColumnFilters: optionalOptions._enableFiltering,
              columnVisibility: visibleColumns,
            }}
            muiPaginationProps={{
              rowsPerPageOptions: optionalOptions._rowsPerPageOptions,
            }}
            enableColumnFilters={optionalOptions._enableFiltering}
            enablePagination={optionalOptions._enablePagination}
            enableStickyHeader={optionalOptions._enableStickyHeader}
            enableSorting={optionalOptions._enableSorting}
            enableDensityToggle={optionalOptions._enableDensityToggle}
            enableFullScreenToggle={optionalOptions._enableFullScreenToggle}
            enableHiding={optionalOptions._enableColumnHiding}
            enableGlobalFilter={optionalOptions._enableSearch}
            localization={localization}
            muiTableBodyRowProps={({ row }) => ({
              onClick: optionalOptions._enableModalWindow
                ? () => {
                    setDialogRowData(row.original);
                    setDialogOpen(true);
                  }
                : undefined,
              sx: optionalOptions._enableModalWindow ? { cursor: 'pointer' } : {},
            })}
          />
        )}

        {optionalOptions._enableModalWindow && (
          <PopupDialog
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            dialogRowData={dialogRowData}
          />
        )}
      </Suspense>
    </ThemeProvider>
  );
}
